import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Save, User, Building2, Briefcase, Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { useConsentStatuses, CONSENT_TYPES } from '../hooks/useConsent';

// Specializations — label (ui) ↔ key (enum)
const SPECIALIZATIONS = [
  { key: 'pilates', label: 'Pilates' },
  { key: 'pilates_clinico', label: 'Pilates Clínico' },
  { key: 'fisioterapia_clinica', label: 'Fisioterapia Clínica' },
  { key: 'orthopedics', label: 'Ortopedia e Traumatologia' },
  { key: 'respiratory', label: 'Respiratória e Cardio' },
  { key: 'geriatrics', label: 'Geriatria e Gerontologia' },
  { key: 'pediatrics', label: 'Pediatria' },
  { key: 'urogynecology', label: 'Uroginecologia' },
  { key: 'dermato_funcional', label: 'Dermato funcional' },
  { key: 'aesthetics', label: 'Estética' },
  { key: 'multiple', label: 'Múltiplas áreas' },
  { key: 'other', label: 'Outro' }
];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    crefito: '',
    phone: '',
    professional_email: '',
    specializations: [],
    practice_type: 'clinic',
    clinic_name: '',
    clinic_address: '',
    clinic_cnpj: '',
    clinic_phone: '',
    clinic_email: '',
    use_professional_data_for_clinic: false
  });

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '',
        crefito: user.crefito || '',
        phone: user.phone || '',
        professional_email: user.professional_email || user.email || '',
        specializations: user.specializations || [],
        practice_type: user.practice_type || 'clinic',
        clinic_name: user.clinic_name || '',
        clinic_address: user.clinic_address || '',
        clinic_cnpj: user.clinic_cnpj || '',
        clinic_phone: user.clinic_phone || '',
        clinic_email: user.clinic_email || '',
        use_professional_data_for_clinic: user.use_professional_data_for_clinic || false
      });
      setLoading(false);
    }
  }, [user]);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleSpecialization = (key) => {
    setForm((f) => {
      const has = f.specializations.includes(key);
      return {
        ...f,
        specializations: has
          ? f.specializations.filter((s) => s !== key)
          : [...f.specializations, key]
      };
    });
  };

  const handleAutonomousMirror = (checked) => {
    setForm((f) => {
      const next = { ...f, use_professional_data_for_clinic: checked };
      if (checked) {
        // Pre-fill clinic info from professional info
        next.clinic_name = f.full_name;
        next.clinic_email = f.professional_email;
        next.clinic_phone = f.phone;
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name,
        crefito: form.crefito || null,
        phone: form.phone || null,
        professional_email: form.professional_email || null,
        specializations: form.specializations,
        practice_type: form.practice_type,
        clinic_name: form.clinic_name || null,
        clinic_address: form.clinic_address || null,
        clinic_cnpj: form.clinic_cnpj || null,
        clinic_phone: form.clinic_phone || null,
        clinic_email: form.clinic_email || null,
        use_professional_data_for_clinic:
          form.practice_type === 'autonomous' ? form.use_professional_data_for_clinic : null
      };
      await updateUser(payload);
      toast.success('Configurações salvas com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto pb-12" data-testid="settings-page">
        <div className="mb-8">
          <h1 className={`text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
            Configurações
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
            Gerencie seus dados profissionais e da sua prática
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-6" data-testid="settings-form">
          {/* ========== SECURITY ========== */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" /> Segurança
              </CardTitle>
              <CardDescription>
                Gerencie sua senha de acesso. Todas as alterações são registradas no log de auditoria.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/change-password')}
                data-testid="settings-change-password-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Alterar senha
              </Button>
            </CardContent>
          </Card>

          {/* ========== PROFESSIONAL DATA ========== */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" /> Dados do Profissional
              </CardTitle>
              <CardDescription>
                Informações pessoais e de registro profissional. O e-mail de login é
                gerenciado separadamente por segurança.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>E-mail de login</Label>
                <Input
                  value={user.email || ''}
                  disabled
                  className="bg-slate-100 dark:bg-slate-800"
                  data-testid="settings-login-email"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Este e-mail é usado para entrar na plataforma. Para alterá-lo, entre em contato com o suporte.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Nome completo *</Label>
                  <Input
                    id="full_name"
                    value={form.full_name}
                    onChange={(e) => updateField('full_name', e.target.value)}
                    required
                    data-testid="settings-full-name"
                  />
                </div>
                <div>
                  <Label htmlFor="crefito">CREFITO</Label>
                  <Input
                    id="crefito"
                    placeholder="CREFITO-3/123456-F"
                    value={form.crefito}
                    onChange={(e) => updateField('crefito', e.target.value)}
                    data-testid="settings-crefito"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="professional_email">E-mail profissional</Label>
                  <Input
                    id="professional_email"
                    type="email"
                    placeholder="profissional@clinica.com"
                    value={form.professional_email}
                    onChange={(e) => updateField('professional_email', e.target.value)}
                    data-testid="settings-professional-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Telefone profissional</Label>
                  <Input
                    id="phone"
                    placeholder="+5511999999999"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    data-testid="settings-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Especialidade(s)</Label>
                <div className="grid sm:grid-cols-2 gap-2" data-testid="settings-specializations">
                  {SPECIALIZATIONS.map((s) => {
                    const checked = form.specializations.includes(s.key);
                    return (
                      <label
                        key={s.key}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          checked
                            ? 'border-primary bg-primary/5'
                            : isDark
                              ? 'border-slate-700 hover:border-slate-500'
                              : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleSpecialization(s.key)}
                          data-testid={`specialization-${s.key}`}
                        />
                        <span className="text-sm">{s.label}</span>
                      </label>
                    );
                  })}
                </div>
                {form.specializations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {form.specializations.map((k) => {
                      const label = SPECIALIZATIONS.find((s) => s.key === k)?.label || k;
                      return (
                        <Badge key={k} variant="secondary" className="text-xs">
                          {label}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ========== PRACTICE / CLINIC DATA ========== */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" /> Dados da Clínica / Atuação
              </CardTitle>
              <CardDescription>
                Como você atua? Isso ajuda a personalizar PDFs e documentação.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <RadioGroup
                value={form.practice_type}
                onValueChange={(v) => updateField('practice_type', v)}
                className="flex flex-col sm:flex-row gap-3"
                data-testid="settings-practice-type"
              >
                <label
                  className={`flex items-center gap-2 p-3 rounded-lg border flex-1 cursor-pointer ${
                    form.practice_type === 'clinic'
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <RadioGroupItem value="clinic" id="practice-clinic" />
                  <Building2 className="w-4 h-4" />
                  <span className="font-medium">Clínica / Empresa</span>
                </label>
                <label
                  className={`flex items-center gap-2 p-3 rounded-lg border flex-1 cursor-pointer ${
                    form.practice_type === 'autonomous'
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <RadioGroupItem value="autonomous" id="practice-autonomous" />
                  <User className="w-4 h-4" />
                  <span className="font-medium">Profissional Autônomo</span>
                </label>
              </RadioGroup>

              {/* CLINIC FIELDS */}
              {form.practice_type === 'clinic' && (
                <div className="space-y-4" data-testid="clinic-fields">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="clinic_name">Nome da Clínica *</Label>
                      <Input
                        id="clinic_name"
                        value={form.clinic_name}
                        onChange={(e) => updateField('clinic_name', e.target.value)}
                        required
                        data-testid="settings-clinic-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clinic_cnpj">CNPJ</Label>
                      <Input
                        id="clinic_cnpj"
                        placeholder="00.000.000/0000-00"
                        value={form.clinic_cnpj}
                        onChange={(e) => updateField('clinic_cnpj', e.target.value)}
                        data-testid="settings-clinic-cnpj"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="clinic_address">Endereço completo</Label>
                    <Textarea
                      id="clinic_address"
                      rows={2}
                      value={form.clinic_address}
                      onChange={(e) => updateField('clinic_address', e.target.value)}
                      data-testid="settings-clinic-address"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="clinic_phone">Telefone de contato</Label>
                      <Input
                        id="clinic_phone"
                        placeholder="+5511999999999"
                        value={form.clinic_phone}
                        onChange={(e) => updateField('clinic_phone', e.target.value)}
                        data-testid="settings-clinic-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clinic_email">E-mail empresarial</Label>
                      <Input
                        id="clinic_email"
                        type="email"
                        placeholder="contato@clinica.com"
                        value={form.clinic_email}
                        onChange={(e) => updateField('clinic_email', e.target.value)}
                        data-testid="settings-clinic-email"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* AUTONOMOUS FIELDS */}
              {form.practice_type === 'autonomous' && (
                <div className="space-y-4" data-testid="autonomous-fields">
                  <div
                    className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-2"
                  >
                    <Checkbox
                      id="use-prof-data"
                      checked={form.use_professional_data_for_clinic}
                      onCheckedChange={(v) => handleAutonomousMirror(Boolean(v))}
                      data-testid="use-professional-data-checkbox"
                    />
                    <Label htmlFor="use-prof-data" className="cursor-pointer">
                      Usar os mesmos dados do perfil profissional?
                    </Label>
                  </div>

                  {!form.use_professional_data_for_clinic && (
                    <>
                      <div>
                        <Label htmlFor="auto_name">Nome comercial / fantasia</Label>
                        <Input
                          id="auto_name"
                          value={form.clinic_name}
                          onChange={(e) => updateField('clinic_name', e.target.value)}
                          data-testid="settings-auto-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="auto_address">Endereço de atuação</Label>
                        <Textarea
                          id="auto_address"
                          rows={2}
                          value={form.clinic_address}
                          onChange={(e) => updateField('clinic_address', e.target.value)}
                          data-testid="settings-auto-address"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="auto_phone">Telefone</Label>
                          <Input
                            id="auto_phone"
                            value={form.clinic_phone}
                            onChange={(e) => updateField('clinic_phone', e.target.value)}
                            data-testid="settings-auto-phone"
                          />
                        </div>
                        <div>
                          <Label htmlFor="auto_email">E-mail</Label>
                          <Input
                            id="auto_email"
                            type="email"
                            value={form.clinic_email}
                            onChange={(e) => updateField('clinic_email', e.target.value)}
                            data-testid="settings-auto-email"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* P3 — Privacy / LGPD preferences */}
          <PrivacyCard isDark={isDark} />

          {/* SAVE BUTTON */}
          <div className="flex justify-end sticky bottom-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-akali text-white hover:opacity-90 shadow-lg"
              data-testid="settings-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Salvar Configurações
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}


// P3 — LGPD preferences card. Renders under the main settings form but is
// self-contained: its own status fetch + toggle. Global preference; per-consultation
// override lives inside the consultation flow.
function PrivacyCard({ isDark }) {
  const { statuses, recordConsent, loading } = useConsentStatuses({ enabled: true });
  const anonymEffective = statuses?.anonymizationEffective;
  const [saving, setSaving] = useState(false);

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      await recordConsent({
        type: CONSENT_TYPES.PATIENT_DATA_ANONYMIZED,
        granted: Boolean(checked),
        contentVersion: null,
      });
      toast.success(checked
        ? 'Anonimização ativada para todas as consultas.'
        : 'Anonimização desativada. Você poderá reativar por-consulta se preferir.');
    } catch (err) {
      toast.error('Não foi possível salvar a preferência.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={isDark ? 'bg-slate-800/60 border-slate-700' : ''}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Privacidade e LGPD</CardTitle>
        </div>
        <CardDescription>
          Controle como os dados do paciente são enviados aos serviços de IA.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-slate-900 dark:text-white">
              Anonimizar dados antes de enviar à IA
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Nome do paciente, telefone, e-mail e CPF são substituídos por
              marcadores antes de qualquer chamada ao Gemini/Groq. O prontuário
              armazenado por você mantém o texto original.
            </p>
            {statuses && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Status atual: <strong>{anonymEffective ? 'Ativada' : 'Desativada'}</strong>
              </p>
            )}
          </div>
          <Switch
            checked={Boolean(anonymEffective)}
            onCheckedChange={handleToggle}
            disabled={loading || saving}
            data-testid="settings-anonymize-toggle"
          />
        </div>
      </CardContent>
    </Card>
  );
}
